import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  Paper,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import {
  saveModifiedNotes,
  selectDocument,
  selectDocumentAtIndex,
  selectPage,
  updateNotes,
} from '../../actions/documents';
import {
  getDocumentsAsSortedArray,
  getSelectedDocument,
  getSelectedPage,
  getTotalNumberOfPagesInCurrentPdf,
  getTotalNumberOfPdfs,
} from '../../selectors/documents';
import { RootState } from '../../store';
import PositionAndAlignmentButtons from '../AnnotationTools/PositionAndAlignmentButtons';
import ResetAndUndoButtons from '../AnnotationTools/ResetAndUndoButtons';
import DocumentQueueList from '../Tools/DocumentQueueList';
import Counter from './Counter';
import DocumentManagerButtons from './DocumentManagerButtons';
import DownloadPdfs from './DownloadPdfs';
import FlagNotes from './FlagNotes';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  readonly expanded: boolean;
}

class DocumentTools extends React.Component<Props, State> {
  state: State = {
    expanded: true,
  };

  handleExpandClick = () => {
    this.setState((prevState) => ({ expanded: !prevState.expanded }));
  };

  selectDocumentAndPage = (documentId: number, pageIndex: number) => {
    this.props.selectDocument(documentId);
    this.props.selectPage(pageIndex);
  };

  render() {
    const { notes } = this.props;

    return (
      <div>
        <Card sx={{ maxWidth: 400, 'z-index': 1 }}>
          <Paper>
            <CardActions onClick={this.handleExpandClick} sx={{ display: 'flex', height: 60, pt: 1 }} disableSpacing>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <InsertDriveFileIcon sx={{ color: '#ffffff' }} />
                  </Avatar>
                }
                title="Document Tools"
              />
              <IconButton
                sx={{
                  transform: this.state.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: (theme: Theme) =>
                    theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shortest,
                    }),
                  marginLeft: 'auto',
                }}
                aria-expanded={this.state.expanded}
                aria-label="Show more"
              >
                <ExpandMoreIcon />
              </IconButton>
            </CardActions>
            <CardActions sx={{ p: 0 }}>
              <DocumentManagerButtons />
            </CardActions>
          </Paper>
          <Collapse in={this.state.expanded} timeout="auto" unmountOnExit>
            <CardContent sx={{ pt: 1, px: 0, bgcolor: 'backContrast.dark' }}>
              <div>
                <Typography sx={{ ml: 3, mt: 1 }} variant="caption">
                  Annotation Options
                </Typography>
                <Box
                  sx={{
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderTopColor: 'backContrast.light',
                    borderBottomColor: 'backContrast.light',
                    mb: 2,
                  }}
                >
                  <PositionAndAlignmentButtons />
                  <Divider sx={{ bgcolor: 'backContrast.light' }} />
                  <ResetAndUndoButtons />
                </Box>
              </div>
              <Counter
                currentPdfIndex={this.props.selectedDocument ? this.props.selectedDocument.index : 0}
                currentPageNumber={this.props.selectedPage ? this.props.selectedPage.pageNumber : 0}
                totalNumberOfPdfs={this.props.totalNumberOfPdfs}
                totalNumberOfPagesInCurrentPdf={this.props.totalNumberOfPagesInCurrentPdf}
                selectDocumentAtIndex={this.props.selectDocumentAtIndex}
                selectPage={this.props.selectPage}
                key={
                  `${this.props.selectedDocument ? this.props.selectedDocument.index : 0}-` +
                  `${this.props.selectedPage ? this.props.selectedPage.pageNumber : 0}`
                }
              />
              <DocumentQueueList
                withPages
                documents={this.props.documents}
                selectedDocument={this.props.selectedDocument}
                selectedPage={this.props.selectedPage}
                onDocumentClick={this.props.selectDocument}
                onPageClick={this.selectDocumentAndPage}
              />
              <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap', m: 3 }} noValidate autoComplete="off">
                <TextField
                  id="trussDesignModificationsBox"
                  rows="4"
                  multiline
                  fullWidth
                  variant="filled"
                  label="Truss Design Modifications"
                  placeholder="Type your notes here..."
                  sx={{ width: '100%' }}
                  value={notes}
                  onChange={(event) => this.props.updateNotes(this.props.activeDocumentId!, event.target.value)}
                  onBlur={this.props.saveModifiedNotes}
                />
              </Box>
              <FlagNotes />
              <Box sx={{ mx: 3 }}>
                <Typography variant="caption">Download Options</Typography>
                <DownloadPdfs />
              </Box>
            </CardContent>
          </Collapse>
        </Card>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const { activeDocumentId } = state.documents;
  const selectedDocument = state.documents.pdfData[activeDocumentId!];
  const notes = selectedDocument == null ? '' : selectedDocument.notes || '';

  return {
    notes,
    activeDocumentId,
    documents: getDocumentsAsSortedArray(state),
    documentsById: state.documents.pdfData,
    selectedDocument: getSelectedDocument(state),
    selectedPage: getSelectedPage(state),
    totalNumberOfPdfs: getTotalNumberOfPdfs(state),
    totalNumberOfPagesInCurrentPdf: getTotalNumberOfPagesInCurrentPdf(state),
  };
};

const actionCreators = {
  selectDocument,
  selectDocumentAtIndex,
  selectPage,
  updateNotes,
  saveModifiedNotes,
};

export default connect(mapStateToProps, actionCreators)(DocumentTools);
